<template>
  <FormCarousel ref="carouselControls">
    <FormCarouselItem name="paymentMethods" label="Which payment methods do you need?">
      <payment-method-selector v-model="siteForm.paymentMethods" />
    </FormCarouselItem>

    <FormCarouselItem name="features" :label="t('onboarding.site.features.title')">
      <v-row>
        <v-col cols="12" xl="6" class="d-flex flex-column ga-6">
          <v-sheet
            class="px-4 pt-2 pb-6 rounded-xl elevation-1 cursor-pointer"
            @click="siteForm.enableMOTO = !siteForm.enableMOTO"
          >
            <v-checkbox
              v-model="siteForm.enableMOTO"
              color="purple-4"
              :label="t('onboarding.site.features.enable_moto_label')"
            />
            <p class="ms-3 text-caption font-weight-light">
              {{ t("components.onboarding.siteDevice.verificationMessage") }}
            </p>
          </v-sheet>
          <v-sheet
            class="px-4 pt-2 pb-6 rounded-xl elevation-1 cursor-pointer"
            @click="siteForm.enablePBL = !siteForm.enablePBL"
          >
            <v-checkbox
              v-model="siteForm.enablePBL"
              color="purple-4"
              :label="t('onboarding.site.features.enable_pbl_label')"
            />
            <p class="ms-3 text-caption font-weight-light">
              {{ t("components.onboarding.siteDevice.verificationMessage") }}
            </p>
          </v-sheet>
        </v-col>
        <v-col cols="12" xl="6">
          <v-sheet
            class="px-4 pt-2 pb-6 rounded-xl elevation-1 d-flex flex-column cursor-pointer"
            @click="enableTips = !enableTips"
          >
            <v-checkbox
              v-model="enableTips"
              color="purple-4"
              :label="t('onboarding.site.features.enable_tips_label')"
            />
            <v-item-group
              v-if="enableTips"
              v-model="tipProvider"
              mandatory
              :class="`mb-5 ga-4 d-flex ${xs ? 'flex-column' : ''}`"
              @click.stop
            >
              <v-item v-slot="{ isSelected, toggle }" value="yetipay">
                <button
                  v-ripple
                  :class="`form-disable-enter pa-5 rounded-xl d-flex justify-space-between cursor-pointer elevation-1 ${isSelected ? 'bg-pink-4' : ''}`"
                  :style="`flex: 1 0 ${xs ? '100%' : 'auto'}`"
                  @click="toggle"
                >
                  <p class="w-100 text-center">yetipay</p>
                </button>
              </v-item>
              <v-item v-slot="{ isSelected, toggle }" value="tipjar">
                <button
                  v-ripple
                  :class="`form-disable-enter pa-5 rounded-xl d-flex justify-space-between cursor-pointer elevation-1 ${isSelected ? 'bg-pink-4' : ''}`"
                  :style="`flex: 1 0 ${xs ? '100%' : 'auto'}`"
                  @click="toggle"
                >
                  <p class="w-100 text-center">TiPJAR</p>
                </button>
              </v-item>
            </v-item-group>
            <p class="ms-3 text-caption font-weight-light">
              {{ t("components.onboarding.siteDevice.tippingInfo") }}
            </p>
          </v-sheet>
        </v-col>
      </v-row>
    </FormCarouselItem>

    <FormCarouselItem
      name="devices"
      label="Which card machines would you like to order?"
      :disable-next-if="(deviceOrder?.s1f2 ?? 0) + (deviceOrder?.s1e2 ?? 0) < 1"
      fill-all-cols
    >
      <DeviceOrderBasket v-model:form="deviceOrder" />
    </FormCarouselItem>

    <!-- TODO: option for customers with existing site name and address from company house -->
    <FormCarouselItem
      name="siteName"
      :label="t('onboarding.site.name.title')"
      :disable-next-if="v$.name.$invalid"
    >
      <y-text-field
        v-model="siteForm.name"
        class="mb-5"
        autocapitalize
        persistent-hint
        persistent-placeholder
        persistent-counter
        counter="22"
        :label="t('onboarding.site.name.official_name_label')"
        :placeholder="t('onboarding.site.name.official_name_placeholder')"
        :hint="t('onboarding.site.name.official_name_hint')"
        :error-messages="validationErrors(v$.name.$errors)"
      />
      <y-text-field
        v-model="siteForm.displayName"
        autocapitalize
        persistent-hint
        persistent-placeholder
        persistent-counter
        counter="50"
        :label="t('onboarding.site.name.display_name_label')"
        :placeholder="t('onboarding.site.name.display_name_placeholder')"
        :hint="t('onboarding.site.name.display_name_hint')"
        :error-messages="validationErrors(v$.displayName.$errors)"
      />
    </FormCarouselItem>

    <FormCarouselItem
      name="siteAddress"
      label="What's the address of your site?"
      :next-loading="submitting"
      :disable-next-if="v$.address.$invalid"
      @next="setDeliveryAddress"
    >
      <OnboardingAddress v-model="siteForm.address" :validator="v$.address" />
      <v-slide-y-transition>
        <div v-if="deliverToDifferentAddress">
          <h3 class="text-h6 my-5">
            {{ t("components.onboarding.siteDevice.deliveryAddress") }}
          </h3>
          <OnboardingAddress v-model="siteForm.deliveryAddress" :validator="v$.deliveryAddress" />
        </div>
      </v-slide-y-transition>
      <y-textarea
        v-model="deliveryNotes"
        :label="t('onboarding.site.deliveryAddress.delivery_notes_label')"
        :placeholder="t('onboarding.site.deliveryAddress.delivery_notes_placeholder')"
        persistent-placeholder
        persistent-counter
        rows="3"
        counter="250"
        class="mt-2"
      />
      <v-checkbox
        v-model="deliverToDifferentAddress"
        label="Deliver my device(s) to a different address?"
        inset
        color="primary"
      />
    </FormCarouselItem>

    <FormCarouselItem
      name="pointOfContact"
      :label="t('onboarding.site.pointOfContact.title')"
      :next-loading="submitting"
      :auto-next="false"
      :disable-next-if="v$.pointOfContact.$invalid"
      @next="submit"
    >
      <div class="d-flex flex-column ga-2">
        <y-text-field
          v-model="siteForm.pointOfContact.name"
          autocapitalize
          :label="t('onboarding.site.pointOfContact.name_label')"
          :error-messages="validationErrors(v$.pointOfContact.name.$errors)"
        />
        <y-phone-number v-model="siteForm.pointOfContact.phoneNumber" class="flex-grow-1" />
        <y-text-field
          v-model="siteForm.pointOfContact.email"
          autocapitalize
          :label="t('onboarding.site.pointOfContact.email_label')"
          :error-messages="validationErrors(v$.pointOfContact.email.$errors)"
        />
        <y-text-field
          v-model="siteForm.pointOfContact.role"
          autocapitalize
          :label="t('onboarding.site.pointOfContact.role_label')"
          :error-messages="validationErrors(v$.pointOfContact.role.$errors)"
        />
      </div>
    </FormCarouselItem>

    <FormCarouselItem
      name="stripe"
      :label="t('onboarding.site.stripe.title')"
      :auto-next="false"
      :disabled="!!onboardingStore.draftMerchant?.site?.billingPaymentMethod"
      :disable-next-if="!!!onboardingStore.draftMerchant?.site?.billingPaymentMethod"
    >
      <StripePaymentWidget
        v-if="
          onboardingStore.draftMerchant?.site?.paymentToken &&
          onboardingStore.draftMerchant?.site?.stripePublishableToken
        "
        class="pa-3 rounded-xl bg-background"
        :create-session="createSession"
        @error="onPaymentWidgetError"
      />
    </FormCarouselItem>

    <FormCarouselItem
      name="summary"
      :auto-next="false"
      :next-loading="submitting"
      :disable-next-if="v$.termsAccepted.$invalid"
      :label="t('onboarding.site.review.title')"
      :fill-all-cols="true"
      :no-back-button="!canEdit"
      @next="placeOrder"
    >
      <OnboardingSiteDeviceSummary
        v-model="siteForm.termsAccepted"
        :site="siteForm"
        :device-basket="deviceOrder"
        :can-edit="canEdit"
        @go-to="carouselControls?.goToItemByName"
      />
    </FormCarouselItem>
  </FormCarousel>
</template>

<script setup lang="ts">
import {
  DraftMerchantEntityEnum,
  DraftMerchantStatusEnum,
  PaymentMethod,
} from "@tableyeti/merchant-service";
import useVuelidate from "@vuelidate/core";
import { maxLength, minLength, email, sameAs, requiredIf } from "@vuelidate/validators";
import { parsePhoneNumber } from "awesome-phonenumber";
import { isEqual } from "lodash-es";
import { postcodeValidator } from "postcode-validator";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useDisplay } from "vuetify";
import { FormCarousel } from "../Form";
import OnboardingSiteDeviceSummary from "./OnboardingSiteDeviceSummary.vue";
import { useCompanyHouseApi } from "@/composables/useCompanyHouseApi";
import { DeviceBasket, OnboardingError, useOnboardingStore } from "@/store/onboarding";
import { toCompany } from "@/utils/company";
import {
  siteDisplayNameValidator,
  siteNameValidator,
  i18nRequired,
  withI18nMessage,
} from "@/utils/custom-validators";
import { validationErrors } from "@/utils/format";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const onboardingStore = useOnboardingStore();
const { api: companyHouseApi } = useCompanyHouseApi();
const { xs } = useDisplay();
const carouselControls = ref<InstanceType<typeof FormCarousel>>();
const submitting = ref(false);

const deviceOrder = ref<DeviceBasket>({
  s1f2: onboardingStore?.draftMerchant?.site?.basket?.s1f2 ?? 0,
  s1f2Dock: onboardingStore?.draftMerchant?.site?.basket?.s1f2Dock ?? 0,
  s1e2: onboardingStore?.draftMerchant?.site?.basket?.s1e2 ?? 0,
});
const enableTips = ref(!!onboardingStore?.draftMerchant?.site?.tips);
const tipProvider = ref<"yetipay" | "tipjar">(
  onboardingStore?.draftMerchant?.site?.tips?.provider ?? "yetipay",
);
const deliverToDifferentAddress = ref<boolean>(
  !isEqual(
    onboardingStore?.draftMerchant?.site?.address,
    onboardingStore?.draftMerchant?.site?.deliveryAddress,
  ) && !!onboardingStore?.draftMerchant?.site?.deliveryAddress?.line1,
);
const deliveryNotes = ref<string | null>(
  onboardingStore?.draftMerchant?.site?.deliveryNotes ?? null,
);

onMounted(async () => {
  const draftMerchant = onboardingStore.draftMerchant!;
  const isRedirect = route.query.redirect && route.query.redirect_status === "succeeded";

  //if it's stripe redirect or we have an error during submit, let's jump to summary
  if (
    (isRedirect && draftMerchant.status === DraftMerchantStatusEnum.SiteAdded) ||
    isSiteSubmitError()
  ) {
    router.replace({ query: undefined });
    return carouselControls.value?.goToItemByName("summary");
  }

  //if we are onboarding a UK company, we can fetch the company details from Companies House
  //so we can show the legal entity address and name
  //Note: this is just an educated guess as the legal entity address might differ from the site address
  //if it's the case we will just let the user fill the site address
  try {
    if (
      draftMerchant?.country?.toLowerCase() === "gb" &&
      draftMerchant.entity === DraftMerchantEntityEnum.Company
    ) {
      const res = await companyHouseApi.getCompany(draftMerchant?.registrationNumber ?? "");
      if (res) {
        const company = toCompany(res, draftMerchant.country);
        //22 is the max length for the site name
        const trimName = company.companyName ? company.companyName.substring(0, 22) : "";
        const trimDisplayName = company.companyName
          ? company.companyName.substring(0, 50)
          : undefined;

        //populate if we dont have a value
        if (!siteForm.value.name) {
          siteForm.value.name = trimName;
        }

        if (!siteForm.value.displayName) {
          siteForm.value.displayName = trimDisplayName;
        }

        if (!siteForm.value.address.line1) {
          siteForm.value.address = {
            line1: company.addressLine1 ?? "",
            line2: company.addressLine2 ?? siteForm.value.address.line2,
            city: company.city ?? "",
            stateOrProvince: company.stateOrProvince ?? siteForm.value.address.stateOrProvince,
            postalCode: company.postalCode ?? "",
            country: onboardingStore.draftMerchant?.country ?? "",
          };
        }
      }
    }
  } catch (e) {
    //not a big deal, we'll just manually fill the site name and address
    console.error("Error fetching company details from Companies House", e);
  }
});

const toSiteForm = () => ({
  name: onboardingStore.draftMerchant?.site?.name ?? "",
  displayName: onboardingStore.draftMerchant?.site?.displayName ?? undefined,
  address: {
    line1: onboardingStore.draftMerchant?.site?.address?.line1 ?? "",
    line2: onboardingStore.draftMerchant?.site?.address?.line2 ?? undefined,
    city: onboardingStore.draftMerchant?.site?.address?.city ?? "",
    stateOrProvince: onboardingStore.draftMerchant?.site?.address?.stateOrProvince ?? undefined,
    postalCode: onboardingStore.draftMerchant?.site?.address?.postalCode ?? "",
    country: onboardingStore.draftMerchant?.country ?? "",
  },
  deliveryAddress: {
    line1: onboardingStore.draftMerchant?.site?.deliveryAddress?.line1 ?? "",
    line2: onboardingStore.draftMerchant?.site?.deliveryAddress?.line2 ?? undefined,
    city: onboardingStore.draftMerchant?.site?.deliveryAddress?.city ?? "",
    stateOrProvince:
      onboardingStore.draftMerchant?.site?.deliveryAddress?.stateOrProvince ?? undefined,
    postalCode: onboardingStore.draftMerchant?.site?.deliveryAddress?.postalCode ?? "",
    country: onboardingStore.draftMerchant?.country ?? "",
  },
  paymentMethods:
    onboardingStore.draftMerchant?.site?.paymentMethods &&
    onboardingStore.draftMerchant?.site?.paymentMethods?.length > 0
      ? onboardingStore.draftMerchant?.site?.paymentMethods
      : ([PaymentMethod.Visa, PaymentMethod.Mc] as PaymentMethod[]),
  termsAccepted: onboardingStore.draftMerchant?.site?.termsAccepted ?? false,
  enableMOTO: onboardingStore.draftMerchant?.site?.enableMOTO ?? false,
  enablePBL: onboardingStore.draftMerchant?.site?.enablePBL ?? false,
  pointOfContact: {
    name: onboardingStore.draftMerchant?.site?.pointOfContact?.name ?? "",
    phoneNumber: onboardingStore.draftMerchant?.site?.pointOfContact?.phoneNumber ?? "",
    email: onboardingStore.draftMerchant?.site?.pointOfContact?.email ?? "",
    role: onboardingStore.draftMerchant?.site?.pointOfContact?.role ?? "",
  },
  get deliveryNotes() {
    if (deliveryNotes.value) {
      return deliveryNotes.value;
    }
    return null; //optional field are nullable
  },
  get tips() {
    if (enableTips.value) {
      return { provider: tipProvider.value };
    }
    return null; //optional field are nullable
  },
});

const siteForm = ref<{
  name: string;
  displayName: string | undefined;
  address: {
    line1: string;
    line2: string | undefined;
    city: string;
    stateOrProvince: string | undefined;
    postalCode: string;
    country: string;
  };
  deliveryAddress: {
    line1: string;
    line2: string | undefined;
    city: string;
    stateOrProvince: string | undefined;
    postalCode: string;
    country: string;
  };
  paymentMethods: PaymentMethod[];
  termsAccepted: boolean;
  pointOfContact: {
    name: string;
    phoneNumber: string;
    email: string;
    role: string;
  };
  enableMOTO: boolean;
  enablePBL: boolean;
  deliveryNotes: string | null; //optional field are nullable
  tips: {
    provider: "yetipay" | "tipjar";
  } | null;
}>(toSiteForm());

const isSiteSubmitError = () =>
  onboardingStore.draftMerchant?.status === DraftMerchantStatusEnum.SiteSubmittedError;

const isSummary = computed(() => carouselControls?.value?.curr() === "summary");
const canEdit = computed(() => !isSiteSubmitError());

const rules = computed(() => ({
  name: {
    required: i18nRequired,
    siteName: siteNameValidator,
    minLength: minLength(5),
    maxLength: maxLength(22),
    $autoDirty: true,
  },
  displayName: {
    siteDisplayName: siteDisplayNameValidator,
    minLength: minLength(5),
    maxLength: maxLength(50),
    $autoDirty: true,
  },
  address: {
    line1: { required: i18nRequired, $autoDirty: true },
    city: { required: i18nRequired, $autoDirty: true },
    postalCode: {
      required: i18nRequired,
      postalCodeValidator: (v: string) =>
        postcodeValidator(v, siteForm.value.address?.country ?? "GB"),
      $autoDirty: true,
    },
    country: { required: i18nRequired, $autoDirty: true },
  },
  deliveryAddress: {
    line1: { required: i18nRequired, $autoDirty: true },
    city: { required: i18nRequired, $autoDirty: true },
    postalCode: {
      required: i18nRequired,
      postalCodeValidator: (v: string) =>
        postcodeValidator(v, siteForm.value.deliveryAddress?.country ?? "GB"),
      $autoDirty: true,
    },
    country: { required: i18nRequired, $autoDirty: true },
  },
  paymentMethods: {
    required: i18nRequired,
  },
  pointOfContact: {
    name: {
      required: i18nRequired,
      minLength: minLength(5),
      maxLength: maxLength(250),
      $autoDirty: true,
    },
    phoneNumber: {
      required: i18nRequired,
      phoneNumberValidator: withI18nMessage(
        "form.phoneNumberInvalid",
        () => parsePhoneNumber(siteForm.value.pointOfContact.phoneNumber).valid,
      ),
      $autoDirty: true,
    },
    email: {
      required: i18nRequired,
      email: withI18nMessage("form.emailInvalid", email),
      $autoDirty: true,
    },
    role: {
      required: i18nRequired,
      minLength: minLength(2),
      maxLength: maxLength(50),
      $autoDirty: true,
    },
  },
  termsAccepted: {
    requiredIf: requiredIf(isSummary.value),
    ...(isSummary.value && { sameAs: sameAs(true) }),
    $autoDirty: true,
  },
  deliveryNotes: {
    maxLength: maxLength(250),
    $autoDirty: true,
  },
}));

const v$ = useVuelidate(rules, siteForm);

const submit = async () => {
  carouselControls.value?.clearError();

  v$.value.$touch();
  if (v$.value.$invalid) {
    carouselControls.value?.setError(
      v$.value.$errors[0]?.$message.toString() || "Please ensure all fields are filled correctly",
    );
    return;
  }

  try {
    submitting.value = true;
    await onboardingStore.updateDraftMerchantSite(siteForm.value, deviceOrder.value);
    carouselControls.value?.next();
  } catch (error) {
    if (error instanceof OnboardingError) {
      carouselControls.value?.setError(error.message, error.field ?? "devices");
    } else {
      carouselControls.value?.setError("Something went wrong", "devices");
    }
  } finally {
    submitting.value = false;
  }
};

const createSession = (): Promise<{ paymentToken: string; stripePublicKey: string }> => {
  const draftMerchant = onboardingStore.draftMerchant;
  if (!draftMerchant?.site?.paymentToken || !draftMerchant?.site?.stripePublishableToken) {
    console.error("Missing payment token or stripe public key");
    carouselControls.value?.setError("Something went wrong", "devices");
    return Promise.reject("Missing payment token or stripe public key");
  }

  return Promise.resolve({
    paymentToken: draftMerchant?.site?.paymentToken,
    stripePublicKey: draftMerchant?.site?.stripePublishableToken,
  });
};

const onPaymentWidgetError = (error: { message: string }) => {
  console.log("payment widget error", error);
  const genericErrorMessage =
    "Something went wrong, please try again later. If the issue persists, please contact Yetipay support.";
  carouselControls.value?.setError(error.message || genericErrorMessage, "devices");
};

const setDeliveryAddress = () => {
  if (!deliverToDifferentAddress.value) {
    siteForm.value.deliveryAddress = { ...siteForm.value.address };
  }
};

const placeOrder = async () => {
  carouselControls.value?.clearError();
  try {
    submitting.value = true;
    if (!isSiteSubmitError()) {
      await onboardingStore.acceptTermsAndConditions();
    }
    await onboardingStore.submitDraftMerchantSite();
  } catch (error) {
    if (error instanceof OnboardingError) {
      switch (error.message) {
        case "Site name already in use": {
          carouselControls.value?.setError(
            t("form.error_invalid_site_name", { siteName: siteForm.value.name }),
            "siteName",
          );
          carouselControls.value?.goToItemByName("siteName");
          break;
        }
        default: {
          carouselControls.value?.setError("Something went wrong", "devices");
          break;
        }
      }
    } else {
      carouselControls.value?.setError(t("form.error_something_went_wrong"), "summary");
    }

    console.log(error);
  } finally {
    submitting.value = false;
  }
};
</script>

<style scoped>
.centered-input :deep(input) {
  text-align: center;
}
</style>

<style scoped>
button:focus {
  outline: 1px solid rgba(var(--v-theme-purple-4), 0.5);
}
</style>
